import {type XAxisFormatValues} from '../../generated/graphql';
/** Annotation state and actions remain in one flat purpose group. */
export type GraphAnnotations = {
  activeCount: number;
  isActive: boolean;
  isEnabled: boolean;
  items: import('../vis/annotations').AnnotationItem[];
  markerQueryXMax?: number | null;
  markerQueryXMin?: number | null;
  onActivate?: (id: string) => void;
  onHover?: (id: string | null) => void;
  readHoveredId: () => string | null;
  subscribeToHover: (onChange: () => void) => () => void;
};
import {type ReactNode, type Ref} from 'react';

import type {
  HighlightSource,
  SetHighlight,
  SetHighlights,
} from '../../state/interactState/highlightSource';
import {type ColorPaletteOption} from '../../util/colors';
import {type LegendPosition} from '../../util/plotHelpers/legendTypes';
import {type PlotFontSizeOrAuto} from '../../util/plotHelpers/plotFontSize';
import {type PlotType} from '../../util/plotHelpers/types';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {type ChartData} from '../vis/chartSeries';
import {
  type DerivedPointVisualizationOption,
  type ExcludeOutliersValues,
  type SinglePointRenderMode,
  type TooltipNumberOfRunsOptions,
} from '../WorkspaceDrawer/Settings/types';
import {type GraphRendererSettings} from './LinePlotSettingsContext';
import {type GraphZoom as PreparedGraphZoom} from './zoom/getGraphZoom';

/**
 * Transitional renderer inputs. Saved config and plotSettings
 * remain explicit legacy fields until their feature groups are migrated.
 * Hosts preserve each group's identity while its fields are unchanged.
 */
/** Resolved axes. The saved key remains diagnostic-only. */
export type GraphAxis = {
  outlierPolicy: ExcludeOutliersValues | undefined;
  xKey: string;
  xIsDatetime: boolean;
  xTitle?: string;
  xScale: 'linear' | 'log';
  yTitle?: string;
  yScale: 'linear' | 'log';
};

export type GraphSelection = {
  metrics?: string[];
  /** True when an expression entry exists, including an empty editor entry. */
  hasExpressions: boolean;
  /** Null disables the existing mounted regex-edit grace window. */
  regexDataKey: string | null;
};

/** Display policy. Measured auto font size and data-dependent bar/scatter decisions stay local. */
export type GraphAppearance = {
  fontSize: PlotFontSizeOrAuto;
  isDarkMode: boolean;
};
/** Requested mode and effective fidelity preserve their distinct existing consumers. */
export type GraphRendering = {
  exportMode: boolean;
  forceReactVis: boolean;
  isFullFidelity: boolean;
  pointStrategy: DerivedPointVisualizationOption;
  plotType: PlotType | undefined;
  allowUPlot: boolean;
};

/** Omission hides the title; an absent action leaves static text. */
export type GraphTitleFeature = {
  text: string;
  renderHighlightedText?: (text: string) => ReactNode;
  onActivate?: () => void;
};

/** Scalar run identity passed to host navigation policy. */
export type GraphRunLinkTarget = {
  runName: string;
  entityName?: string;
  projectName?: string;
  isGrouped?: boolean;
};

export type GraphLegendLinkTarget = {legendHref?: string};

/** Tooltip formatting and optional host actions; omission retains values without actions. */
export type GraphTooltip = {
  colorPalette: ColorPaletteOption;
  xValueFormat?: XAxisFormatValues;
  colorRunNames?: boolean;
  showFullRunName?: boolean;
  highlightedCompanionRunOnly?: boolean;
  runVisibility?: TooltipNumberOfRunsOptions;
  runNameTruncation?: TruncationType;
  showXRange?: boolean;
  resolveRunLink?: (target: GraphRunLinkTarget) => string | undefined;
  onActivateRunLink?: (href: string) => void;
  /** Displayed X coordinate; the host owns axis-specific effects. */
  onSnapToX?: (x: number) => void;
  showSnapToXHint?: boolean;
};

export const DEFAULT_GRAPH_LEGEND_POSITION: LegendPosition = 'north';

/** Omission disables the legend; an empty group enables adaptive defaults. */
export type GraphLegendFeature = {
  colorPalette?: ColorPaletteOption;
  colorRunNames?: boolean;
  isSingleRun: boolean;
  onHighlight?: (
    runId: string | undefined,
    metricName: string | undefined
  ) => void;
  position?: LegendPosition;
  resolveRunLink?: (target: GraphLegendLinkTarget) => string | undefined;
  show?: boolean;
};

/** Independent optional leaves; omission needs no disabled values or callbacks. */
export type GraphNotices = {
  displayRowLimitPerRunset?: number;
  fetchedRunCount?: number;
  groupedIncludedRunCount?: number;
  groupInputRunLimitPerRunset?: number;
  groupingDepth?: number;
  hasGroupedRunsets?: boolean;
  hasSubsampledGroups?: boolean;
  isDelayed?: true;
  isSamplingFallback?: boolean;
  maxFetchedRunsPerRunset?: number;
  metricCount?: number;
  onDelayedNoticeShown?: () => void;
  onLoadAnyway?: () => void;
  runsetCount?: number;
  samplingWarnings?: string[];
  showLimitHelp?: boolean;
};

export type GraphRenderProps = {
  /** Stable presentation policy, separate from numeric geometry. */
  appearance: GraphAppearance;
  /** Effective rendering choices, requested fidelity, and SVG export. */
  rendering: GraphRendering;
  /** Host-selected facts for diagnostics and regex settling, independent of query envelopes. */
  errors: GraphErrors;
  loading: GraphLoading;
  /** Readiness gates the provider; loading retains stale data and existing error precedence. */
  /** Resolved axis settings and titles, stable while their values are unchanged. */
  axis: GraphAxis;
  fallbacks?: GraphFallbacks;
  /** Raw saved key used only in diagnostics and test attributes. */
  /** Saved options, including the raw xAxis used by diagnostics. */
  /** Numeric identity is independent of descriptions and drawing style. */
  series: ChartData['series'];
  seriesStylesById: ChartData['seriesStylesById'];
  seriesMetadataById: ChartData['seriesMetadataById'];
  /** Formatting and capabilities are independent of legend links and shared metric mode. */
  tooltip?: GraphTooltip;
  notices?: GraphNotices;
  /** Optional legend configuration; shared metric mode remains independent. */
  legend?: GraphLegendFeature;
  /**
   * Cross-panel highlight: a source over the Redux `interactState` highlight
   * and its two writers. GraphContainer takes the store's one source and the
   * writers from the `interactState` hooks. All three are stable for the
   * panel's life, so a highlight change never re-renders Graph; readers below
   * subscribe to the axes they draw. Hovering a legend run calls
   * `setHighlight`, and moving the pointer over the chart calls
   * `setHighlights`. The writers must update the source, or the crosshair
   * never shows its flag. Panels that share a crosshair share one source.
   */
  highlight: {
    setHighlight: SetHighlight;
    setHighlights: SetHighlights;
    source: HighlightSource;
  };
  /** Optional geometry, display state, stable hover source, and separate selection events. */
  annotations?: GraphAnnotations;
  /**
   * ID of the mounted panel, from PanelIDContext. Stable for the panel's
   * life and unique per mounted panel: it keys the error panel's RUM
   * de-duplication, the zoom usage counter, and chart telemetry, and is sent
   * with the Delay Render RUM actions. The only copy in GraphRenderProps.
   */
  panelId: string;
  /** Host-resolved title text, search decoration, and optional activation. */
  title?: GraphTitleFeature;
  /** Remaining internal display and notice settings; no navigation or media services. */
  plotSettings: Omit<
    GraphRendererSettings,
    | 'xAxis'
    | 'xAxisIsDatetime'
    | 'xAxisTitle'
    | 'xLogScale'
    | 'yAxisTitle'
    | 'yLogScale'
    | keyof GraphAppearance
    | keyof GraphRendering
    | 'pointVisualizationMethod'
    | 'uplotEnabled'
  >;
  /**
   * Ref Graph attaches to the layout grid so the host can measure the panel,
   * from PanelInteractionContext. Stable for the panel's life; null is
   * accepted.
   */
  sizeRef: Ref<HTMLDivElement>;
  /** Controlled displayed domains and semantic gestures; host preparation owns effects. */
  zoom: GraphZoom;
};

export type GraphFallbacks = {
  barYMin?: number | null;
  barYMax?: number | null;
  singlePointStyle?: SinglePointRenderMode;
};

/** Displayed domains and dedicated zoom actions; query flags retain query-space meaning. */
export type GraphZoom = {
  isActive: boolean;
  isZooming: boolean;
  isZoomedXMax: boolean;
  isZoomedXMin: boolean;
  onReset?: () => void;
  onZoom: PreparedGraphZoom['onZoom'];
  xDomain: PreparedGraphZoom['xDomain'];
  yDomain: PreparedGraphZoom['yDomain'];
};

export type GraphErrors = {
  hasExpressions: boolean;
  hasUnexpectedError?: boolean;
  metricKeys?: string[];
  savedXKey?: string;
};
export type GraphLoading = {
  isActive: boolean;
  isReady: boolean;
  regexSettlingKey: string | null;
};

/** Shared metric mode projected for feature consumers. */
export type GraphPanel = {id: string; isMultiMetric: boolean};
